import dayjs from "dayjs";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { FormCalendarField } from "../../components/form/FormCalendarField";
import { FormCheckboxField } from "../../components/form/FormCheckboxField";
import { FormInputField } from "../../components/form/FormInputField";
import { icCheck } from "../../components/image/image-src";
import { OkCancelPopup } from "../../components/modal/OkCancelPopup";
import { UiTitle } from "../../components/text/UiTitle";
import { TUsePopupHook, usePopup } from "../../hooks/usePopup";
import axiosInstance from "../../services/axiosInstance";
import { toNumberString } from "../../utils/common";
import { toNum } from "../../utils/conf";

const roomTypes: RoomType[] = ["ONE_BED", "TWO_BEDS", "SUITE"];
const breakfastOptions: BreakfastOption[] = ["INCLUDED", "NOT_INCLUDED"];

const sendHotelInquiry = async (data: HotelInquiryFormProps) => {
  const response = await axiosInstance("/hotel-inquiry", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    data: {
      ...data,
      checkInDate: dayjs(data.checkInDate).format("YYYY-MM-DD"),
      checkOutDate: dayjs(data.checkOutDate).format("YYYY-MM-DD"),
    },
  });
  return response.data;
};

const useSendHotelInquiry = ({ onSuccess }: { onSuccess?: () => void }) => {
  return useMutation({
    mutationFn: sendHotelInquiry,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      alert(`Error sending inquiry`);
    },
  });
};

const HotelInquiry: React.FC = () => {
  const { t } = useTranslation();
  const popup = usePopup({
    onOk: () => {
      window.location.reload();
    },
  });
  const sendHotelInquiry = useSendHotelInquiry({
    onSuccess: () => {
      popup.open();
    },
  });
  const { register, handleSubmit, control, watch, setValue, formState } =
    useForm<HotelInquiryFormProps>({
      mode: "onChange",
      defaultValues: {
        fullName: "",
        email: "",
        location: "",
        hotelName: "",
        checkInDate: undefined,
        checkOutDate: undefined,
        quantityOfGuests: 1,
        roomTypes: [],
        breakfastOption: "INCLUDED",
      },
    });

  const isValid =
    formState.isValid &&
    watch("roomTypes")?.length > 0 &&
    watch("checkInDate")!.getTime() < watch("checkOutDate")!.getTime();
  const onSubmit = (data: HotelInquiryFormProps) => {
    sendHotelInquiry.mutate(data);
  };

  const renderInputController = (
    name: keyof HotelInquiryFormProps,
    label: string,
    placeholder: string,
    type?: string,
  ) => (
    <Controller
      name={name}
      control={control}
      rules={{ required: true }}
      render={({ field }) => (
        // @ts-expect-error: Type 'string' is not assignable to type 'number | undefined'
        <FormInputField
          label={label}
          placeholder={placeholder}
          type={type}
          {...field}
        />
      )}
    />
  );

  const renderCalendarController = (
    name: "checkInDate" | "checkOutDate",
    label: string,
  ) => (
    <Controller
      name={name}
      control={control}
      rules={{ required: true }}
      render={({ field }) => (
        <FormCalendarField
          label={t(label)}
          placeholder={t("dd/mm/yyyy")}
          date={field.value}
          onChangeDate={(date: Date) => {
            field.onChange(date);
            setValue(name, date);
          }}
        />
      )}
    />
  );

  return (
    <div className="pb-[3.5rem] pt-8">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="ui-container flex !max-w-[55rem] flex-col"
      >
        <div className="mb-8">
          <UiTitle text={t("Hotel Inquiry Form")} />
          <p className="mt-1.5 text-sm leading-[24px] text-[#5E6D77]">
            {t("Request a hotel booking by filling out the form below.")}
          </p>
        </div>

        <div className="shadow-form flex flex-col gap-4 rounded-2xl rounded-[20px] bg-white p-5 [box-shadow:0px_1px_12px_0px_#00000014] sm:gap-[32px] sm:p-[32px]">
          <div className="flex w-full flex-col gap-4 sm:flex-row sm:gap-6">
            {renderInputController(
              "fullName",
              t("Full Name (English)"),
              t("Please enter your name"),
            )}
            {renderInputController(
              "email",
              t("Email"),
              t("Please enter your email"),
              t("email"),
            )}
          </div>

          <div className="flex w-full flex-col gap-4 sm:flex-row sm:gap-6">
            {renderInputController(
              "location",
              t("Location Inquiry"),
              t("Please enter location"),
            )}
            {renderInputController(
              "hotelName",
              t("Hotel name"),
              t("Please enter hotel name"),
            )}
          </div>

          <div className="flex w-full flex-col gap-4 sm:flex-row sm:gap-6">
            {renderCalendarController("checkInDate", "Check-in date")}
            {renderCalendarController("checkOutDate", "Check-out date")}
          </div>

          <div className="flex w-full gap-6">
            <Controller
              name="quantityOfGuests"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormInputField
                  label={t("Quantity of Guests")}
                  placeholder={t("Please enter the quantity")}
                  value={toNum(field.value)}
                  onChange={(e) => {
                    const value = +toNumberString(e.target.value);
                    field.onChange(value);
                    setValue("quantityOfGuests", value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                />
              )}
            />
          </div>

          <div className="flex w-full gap-6">
            <FormCheckboxField
              type="checkbox"
              label={t("Room Type")}
              name="roomTypes"
              options={roomTypes.map((roomType) => ({
                label: t(roomType),
                value: roomType,
                checked: watch("roomTypes")?.includes(roomType),
              }))}
              onChange={(e) => {
                const value = e.target.value as RoomType;
                const current = watch("roomTypes") || [];
                const updated = e.target.checked
                  ? [...current, value]
                  : current.filter((r) => r !== value);
                setValue("roomTypes", updated);
              }}
            />

            <FormCheckboxField
              type="radio"
              label={t("Breakfast")}
              name="breakfastOption"
              options={breakfastOptions.map((option) => ({
                label: t(option),
                value: option,
                checked: watch("breakfastOption") === option,
              }))}
              onChange={(e) => {
                setValue("breakfastOption", e.target.value as BreakfastOption);
              }}
            />
          </div>

          <button
            type="submit"
            disabled={!isValid}
            className="h-[50px] rounded-[50px] bg-[#A8C6FF] text-base font-medium leading-[20px] text-[#232323] disabled:bg-[#BFC5C9] disabled:text-white"
          >
            {t("Send Inquiry")}
          </button>
        </div>
        {popup.isOpen && <TtPopupSuccess popup={popup} />}
      </form>
    </div>
  );
};

export default HotelInquiry;

const TtPopupSuccess = ({ popup }: { popup: TUsePopupHook }) => {
  const { t } = useTranslation();
  return (
    <OkCancelPopup
      ic={icCheck}
      title={t("Inquiry sent successfully!")}
      onOk={() => popup.onOk()}
      onCancel={undefined}
      okText={t("Ok")}
    />
  );
};
